import { useEffect, useState } from 'react'
import { ChevronRight, Clock, ShieldCheck, Sparkles } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import {
  useHubData, HubHero, ServiceStateLine, AttentionSection, ActivitySection,
  heroMood, type HubService,
} from './FamilyHub.js'
import {
  AIDE_SCANS, FreshnessLine, heureFr, honnete, type SoireeProjection, type SoireeView,
  type TicketingFreshness,
} from '../soirees/lib.js'
import { NuitBilletteries } from '../soirees/NuitBilletteries.js'
import {
  fraicheursDeLaNuit, tuileBilletterie,
  type AffichageBilletteries, type VueNuit,
} from '../soirees/nuit-billetteries-model.js'
import { useMenuSoiree } from '../soirees/menu-soiree.js'

/** Ce que `/api/v1/soirees/current` rend à la carte (les champs qu'elle lit). */
interface SoireeCourante {
  soiree: SoireeView | null
  freshness: TicketingFreshness
  /** Une soirée association : la soirée et ses rattachés, comptés ensemble (20/10/2026). */
  night_soiree?: VueNuit | null
  soiree_association?: object | null
  billetteries?: AffichageBilletteries
}

/**
 * /security — le hub Sécurité (LOT 5, référence artistique des hubs).
 *
 * « Voilà où en est la sécurité du Live maintenant. » Les données viennent
 * de la Main Courante via le cache Gestion — le navigateur ne lui parle
 * jamais ; les opérations profondes s'ouvrent dans l'application.
 */

/** Métriques les plus utiles d'abord — on n'affiche pas tout le payload. */
const METRIC_PRIORITY = [
  'critical_incidents', 'open_incidents', 'agents_on_duty',
  'exits_to_regularize', 'expired_cnaps_cards', 'entries_total',
]

export function pickMetrics(svc: HubService | undefined, priority: string[], max = 4) {
  if (!svc) return []
  const byKey = new Map(svc.metrics.map((m) => [m.key, m]))
  const chosen = priority.map((k) => byKey.get(k)).filter((m) => m !== undefined)
  for (const m of svc.metrics) if (!chosen.includes(m) && chosen.length < max) chosen.push(m)
  return chosen.slice(0, max).map((m) => ({
    label: m!.label,
    value: `${m!.value}${m!.unit ?? ''}`,
    tone: (m!.severity === 'CRITICAL' || m!.severity === 'ERROR') ? 'bad' as const
      : (m!.severity === 'WARNING' || m!.severity === 'ACTION_REQUIRED') ? 'warn' as const
      : undefined,
  }))
}

/**
 * SOIRÉE EN COURS — bloc prioritaire quand le club tourne.
 *
 * TERMINOLOGIE VERROUILLÉE : « billets scannés » / « entrées » — des scans ;
 * les sorties ne sont pas comptabilisées, ce n'est JAMAIS « clients sur
 * place ». Sans permission soiree.read (403) ou sans soirée en cours, le bloc
 * disparaît sans bruit.
 *
 * SOIRÉE ASSOCIATION (20/10/2026) : la même règle que la tuile Billetterie de
 * la fiche. Le 09/09, la porte lisait ici « 0 billet scanné » (Shotgun) toute
 * la nuit, devant 894 entrées Pretix : la carte compte toute la nuit
 * (réglage), avec le détail par billetterie dessous.
 */
function SoireeEnCoursCard() {
  const [data, setData] = useState<SoireeCourante | null>(null)
  const [proj, setProj] = useState<SoireeProjection | null>(null)
  const menuSoiree = useMenuSoiree()

  useEffect(() => {
    let vivant = true
    const charger = () => {
      fetch('/api/v1/soirees/current', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((j: SoireeCourante) => {
          if (!vivant) return
          setData(j)
          if (j.soiree) {
            fetch(`/api/v1/soirees/${j.soiree.id}/projection`, { credentials: 'same-origin' })
              .then((r) => (r.ok ? r.json() : null))
              .then((pj: SoireeProjection | null) => { if (vivant && pj) setProj(pj) })
              .catch(() => undefined)
          }
        })
        .catch(() => undefined)
    }
    charger()
    const t = setInterval(charger, 30_000)
    return () => { vivant = false; clearInterval(t) }
  }, [])

  if (!data?.soiree || data.soiree.phase !== 'ONGOING') return null
  const s = data.soiree
  const t = tuileBilletterie({ scanned: s.tickets_scanned, valid: s.tickets_valid, taux: s.scan_rate }, data.night_soiree, data.soiree_association, data.billetteries)
  const nuitEntiere = t.mode === 'NUIT' && t.nuit !== null
  const fraicheurs = nuitEntiere ? fraicheursDeLaNuit(t.nuit, data.freshness) : null
  return (
    <section aria-labelledby="secu-soiree">
      <h2 className="lc-section-title" id="secu-soiree">Soirée en cours</h2>
      <Link href={`/soirees/${s.id}`} className="so-securite-card lc-card lc-card--violet" {...menuSoiree.soiree(s)}>
        <span className="lc-card__edge" aria-hidden="true" />
        <div className="so-securite-card__head">
          <p className="so-securite-card__name"><Sparkles size={17} aria-hidden="true" /> {s.name}</p>
          <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />{s.phase_label}</span>
        </div>
        {/* La porte scanne déjà pour un événement rattaché : la soirée elle-même commence plus tard. */}
        {s.ouverte_avant ? <p className="lc-hint">{s.ouverte_avant.libelle}</p> : null}
        {/* « Masquer » (réglage) : pas de compteur — jamais un 0 trompeur à la porte. */}
        {t.mode === 'MASQUEE' ? null : (
          <>
            <div className="so-hero__count">
              <strong>{t.valeur?.toLocaleString('fr-FR') ?? '—'}</strong>
              <span className="so-hero__count-label">{t.libelle}</span>
            </div>
            {fraicheurs
              ? <p className={`so-fresh${fraicheurs.perimee ? ' so-fresh--warn' : ''}`}><Clock size={13} aria-hidden="true" />{fraicheurs.texte}</p>
              : <FreshnessLine f={data.freshness} />}
            {/* Le détail par billetterie, sans liens : la carte entière en est déjà un. */}
            {nuitEntiere ? <NuitBilletteries nuit={t.nuit!} sansTete /> : null}
          </>
        )}
        {proj ? (
          <div className="so-securite-card__ops">
            <span><strong>{honnete(proj.security.agents_on_duty)}</strong> agents sur place</span>
            <span><strong>{proj.staff.present_confirmed === 0 && (proj.staff.uncertain ?? 0) > 0 ? '—' : honnete(proj.staff.present_confirmed)}</strong> staff présent{(proj.staff.uncertain ?? 0) > 0 ? ` (+${proj.staff.uncertain} à confirmer)` : ''}</span>
            <span><strong>{honnete(proj.security.open_incidents)}</strong> incidents actifs</span>
            <span className={proj.markers.some((m) => m.type === 'CLOSING_ANNOUNCEMENT') ? 'so-securite-card__closing so-securite-card__closing--done' : 'so-securite-card__closing'}>
              Annonce de fermeture : {(() => {
                const c = proj.markers.find((m) => m.type === 'CLOSING_ANNOUNCEMENT')
                return c ? `donnée à ${heureFr(c.occurred_at)}` : 'pas encore donnée'
              })()}
            </span>
          </div>
        ) : null}
        {/* « Masquer » : aucun chiffre sur la carte, donc pas d'aide sur « ce chiffre ». */}
        {t.mode === 'MASQUEE' ? null : <p className="lc-hint">{AIDE_SCANS}</p>}
        <span className="so-hero__cta">Voir la soirée <ChevronRight size={16} aria-hidden="true" /></span>
      </Link>
    </section>
  )
}

export function SecurityHub() {
  const meta = useShellMeta()
  const data = useHubData('security')
  const mc = data?.services.find((s) => s.key === 'main-courante') ?? data?.services[0]

  const titre = !mc || mc.never_contacted
    ? 'Aucune donnée fiable disponible'
    : mc.status === 'DOWN'
      ? 'Main Courante indisponible'
      : mc.headline?.title ?? 'Sécurité'
  const detail = !mc || mc.never_contacted
    ? 'Gestion n’a pas encore pu joindre la Main Courante — nouvel essai automatique.'
    : mc.status === 'DOWN'
      ? 'Gestion conserve les dernières données fiables et réessaie automatiquement.'
      : mc.headline?.detail

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger hub--security">
        <HubHero
          familyLabel="Sécurité"
          Icon={ShieldCheck}
          title={titre}
          detail={detail}
          mood={heroMood(mc)}
          cta={mc?.primary_action ?? null}
          metrics={mc && !mc.never_contacted ? pickMetrics(mc, METRIC_PRIORITY) : []}
          serviceLine={mc ? <ServiceStateLine svc={mc} /> : null}
        />

        <SoireeEnCoursCard />

        <AttentionSection
          alerts={data?.alerts ?? []}
          total={data?.alerts_active_total ?? 0}
          allLink="/alerts?service=main-courante&state=active"
          Icon={ShieldCheck}
          emptyText={data ? 'Aucune alerte de sécurité active.' : '…'}
        />

        <div className="hub-columns">
          <ActivitySection
            activity={data?.activity ?? []}
            emptyText="Aucune activité récente côté Gestion pour cette famille."
          />
          <section className="lc-panel" aria-label="Application">
            <header className="lc-panel__head"><h2 className="lc-section-title">Application</h2></header>
            <div className="lc-panel__body lc-panel__body--flush hub-soon">
              <div className="hub-soon__row">
                <span className="hub-soon__name">
                  <span className="hub-soon__icon" style={{ background: 'var(--grad-brand)', color: '#fff' }}><ShieldCheck size={17} /></span>
                  Main Courante
                </span>
                {mc && !mc.never_contacted && mc.status === 'OK' && !mc.stale
                  ? <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />Connectée</span>
                  : mc && mc.stale
                    ? <span className="lc-pill lc-pill--warning">Données périmées</span>
                    : <span className="lc-pill lc-pill--idle">{mc?.status ?? 'Aucune donnée'}</span>}
              </div>
            </div>
          </section>
        </div>
      </main>
    </Shell>
  )
}
